import moment from 'moment';
import { nanoid } from 'nanoid';
import { assoc, assocPath, path } from 'ramda';
import { delay, channel } from 'redux-saga';
import {
  all,
  takeEvery,
  takeLatest,
  call,
  put,
  select,
  take,
  race,
  fork,
  join,
  cancel,
  actionChannel,
} from 'redux-saga/effects';

import {
  invalidateAllQuoteFileQueries,
  invalidateAllQuoteHeaderQueries,
  invalidateQuoteFilesQuery,
  invalidateQuoteHeaderQueryKey,
  type QuoteItemBaqData,
  TEMP_PART_UUID_PREFIX,
  QUOTE_STATUS,
} from '@frontend/api';
import { createStandaloneToast, theme } from '@frontend/components';

import { DETAILS_SETTINGS_MODAL_TYPES, QUOTE_ITEM_STATUS } from 'constants/all';
import { LOAD_BAQ } from 'constants/performance';

import { createTemporaryQuoteItems } from 'containers/EditQuote/QuoteFileUploader/utils';

import { detailsSettingsModalFlow } from 'modules/common/modals/detailsSettingsModal/data/sagas';
import { finalizeModal } from 'modules/common/modals/finalizeModal/data/actions';
import { fileUploadSaga } from 'modules/common/modals/uploadDetailsModal/data/sagas';
import { fetchOpDefs } from 'modules/opDefs/data/sagas';
import { fetchProcesses } from 'modules/processes/data/sagas';
import {
  fetchEntities,
  watchFetchGlobalExpediteOptions,
} from 'modules/quotes/quoteDetailsPage/data/sagas';
import { receiveQuoteItemComponent } from 'modules/quotes/quoteItemComponents/data/actions';
import { getExpandedComponentId } from 'modules/quotes/quoteItemComponents/data/reducer';
import {
  collapseQuoteItem,
  componentStatuses,
  initialBriefComponentFetch,
  receiveQuantities,
  receiveQuoteItems,
  receiveQuoteOpDefs,
  receiveQuoteWorkflow,
  receiveTemporaryQuoteItemIds,
  resetQuoteItemsStore,
} from 'modules/quotes/quoteItems/data/actions';
import {
  getExpandedQuoteItemId,
  getTemporaryQuoteItemIds,
} from 'modules/quotes/quoteItems/data/selectors';
import newQuoteItemService from 'modules/quotes/quoteItems/data/service';
import { invalidateAllBomTableQueries } from 'modules/quotes/quoteItems/data/utils/bomTable';

import partService from 'services/partService';
import processesService from 'services/processesService';
import quoteService from 'services/quoteService';

import { getFormData } from 'store/part/utils';
import { getOrCreateQuote } from 'store/quotes/getOrCreateQuote';

import history from 'utils/browserHistory';
import { isBlank, createNumericComparator } from 'utils/helpers';
import { toFloatOrNull } from 'utils/numberUtils';
import { buildLineItemsFileInfo } from 'utils/partUtils';
import { isValidExpedite, isExpediteEqual } from 'utils/quoteUtils';
import { formatQuoteId } from 'utils/quoteUtils';
import { getErrorMessage } from 'utils/stringUtils';

import * as APP from '../app';
import { initMaterialStore } from '../materials/sagas';
import * as SESSION from '../session';
import * as actions from './actions';
import {
  baqPageInitialization,
  changeDueDate,
  changeRequestedDate,
} from './actions';
import * as selectors from './selectors';
import * as types from './types';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeLatest(types.QUOTE_CANCEL_REQUEST, watchCancelQuote);
  yield takeLatest(types.QUOTE_MARK_SENT_REQUEST, watchMarkQuoteSent);
  yield takeLatest(types.QUOTE_MARK_LOST_REQUEST, watchMarkQuoteLost);
  yield takeLatest(types.QUOTE_DELETE_FLOW, watchDeleteQuote);
  yield takeLatest(types.QUOTE_CREATE_REVISION_REQUEST, watchCreateRevision);
  yield takeLatest(types.QUOTE_COPY_REQUEST, watchCopyQuote);
  yield takeLatest(types.QUOTE_CREATE_CSV_REQUEST, watchCreateQuoteCSV);
  yield takeLatest(types.QUOTE_FINALIZE_FLOW, watchFinalizeQuote);
  yield takeLatest(types.QUOTE_SEND_REQUEST, watchSendQuote);
  yield takeLatest(
    types.QUOTE_FINALIZE_SUBMIT_REQUEST,
    watchFinalizeQuoteSubmit,
  );
  yield takeLatest(types.CHANGE_TAX_REQUEST, watchChangeTax);
  yield takeLatest(types.CHANGE_EXPIRED_DATE, watchChangeExpiredDate);
  yield takeLatest(
    types.QUOTES_PRIVATE_NOTES_UPDATE,
    watchQuotesPrivateNotesUpdate,
  );
  yield takeLatest(
    types.CUSTOMER_FACING_NOTES_UPDATE,
    watchCustomerFacingNotesUpdate,
  );
  yield fork(watchQuantityUpdate);
  yield fork(watchExpeditesUpdate);
  yield takeLatest(types.PASTE_EXPEDITE_OPTIONS_ROW, watchPasteExpedites);
  yield takeLatest(types.PASTE_ALL_EXPEDITE_OPTIONS, watchPasteAllExpedites);
  yield takeLatest(types.CLEAR_EXPEDITE_OPTIONS_ROW, watchClearExpedites);
  yield takeLatest(types.CLEAR_ALL_EXPEDITE_OPTIONS, watchClearAllExpedites);
  yield takeLatest(
    types.APPLY_EXPEDITE_OPTIONS_ROW_TO_ALL,
    watchApplyExpeditesToAll,
  );
  yield takeLatest(types.FEATURES_UPDATE_REQUEST, watchFeaturesUpdate);
  yield takeLatest(types.OVERRIDES_UPDATE_REQUEST, watchOverridesUpdate);
  yield takeEvery(types.PUBLIC_NOTES_UPDATE_REQUEST, watchPublicNotesUpdate);
  yield takeEvery(types.PRIVATE_NOTES_UPDATE_REQUEST, watchPrivateNotesUpdate);

  yield takeLatest(types.CONTACT_SELECT_REQUEST, watchSelectContact);
  yield takeLatest(types.ESTIMATOR_SELECT_REQUEST, watchSelectEstimator);
  yield takeLatest(types.SALESPERSON_SELECT_REQUEST, watchSelectSalesperson);
  yield takeLatest(
    types.SUPPLIER_FACILITY_SELECT_REQUEST,
    watchSelectSupplierFacility,
  );
  yield takeLatest(
    types.CHANGE_QUOTE_PRIORITY_REQUEST,
    watchSelectQuotePriority,
  );

  yield takeEvery(types.UPLOAD_REQUEST, watchUpload);
  yield takeEvery(types.UPLOAD_BOM_GENERATION_REQUEST, watchUpload);
  yield takeLatest(types.FETCH_EMAIL_TEMPLATES, fetchEmailTemplatesSaga);
  yield takeLatest(
    types.APPLY_QUOTE_EXPEDITE_OPTIONS_REQUEST,
    watchApplyQuoteExpediteOptions,
  );
  yield takeLatest(
    types.CLEAR_QUOTE_EXPEDITE_OPTIONS_REQUEST,
    watchClearQuoteExpediteOptions,
  );
  yield fork(watchContactConflict);
  yield takeLatest(types.REORDER_QUOTE_ITEMS_FLOW, watchReorderQuoteItemsFlow);
  yield takeLatest(types.DOWNLOAD_NEST_PDF_REQUEST, watchDownloadNestPDF);
  yield takeLatest(types.CHANGE_DUE_DATE_REQUEST, watchChangeDueDate);
  yield takeLatest(
    types.CHANGE_REQUESTED_DATE_REQUEST,
    watchChangeRequestedDate,
  );
  yield takeLatest(types.BAQ_PAGE_INIT_REQUEST, watchBAQInitializing);
  yield takeLatest(
    types.QUOTE_RFQ_NUMBER_UPDATE_REQUEST,
    watchUpdateQuoteRfqNumber,
  );
  yield takeLatest(
    types.QUOTE_METADATA_UPDATE_REQUEST,
    watchUpdateQuoteMetadata,
  );
  yield takeLatest(types.QUOTE_REFRESH_REQUEST, watchRefreshQuote);
  yield takeLatest(types.EXPORT_TO_EXCEL_REQUEST, watchExportQuoteSummary);
  yield takeLatest(
    types.REFETCH_QUOTE_ITEM_HEADERS_REQUEST,
    watchRefetchQuoteItemHeaders,
  );
}

function* watchRefreshQuote() {
  try {
    const quoteNumberWithRevision = yield select(
      selectors.getQuoteNumberWithRevision,
    );

    if (!quoteNumberWithRevision) return;

    const expandedQuoteItemId = yield select(getExpandedQuoteItemId);
    if (expandedQuoteItemId) {
      const expandedComponentId = yield select(getExpandedComponentId);
      if (expandedComponentId) {
        const expandedComponent = yield call(
          quoteService.components.get,
          expandedComponentId,
        );
        yield put(
          receiveQuoteItemComponent({ quoteItemComponent: expandedComponent }),
        );
      }
    }

    invalidateQuoteHeaderQueryKey(quoteNumberWithRevision);
    const quoteId = yield select(selectors.getQuoteId);
    invalidateQuoteFilesQuery(quoteId);

    const quoteItems = yield call(
      quoteService.getQuoteItemHeadersByNumber,
      quoteNumberWithRevision,
    );

    if (expandedQuoteItemId) {
      const expandedQuoteItem = yield call(
        quoteService.quoteItems.get,
        expandedQuoteItemId,
      );
      const expandedQuoteItemsIndex = quoteItems.findIndex(
        ({ id }) => id === expandedQuoteItem.id,
      );
      if (expandedQuoteItemsIndex !== -1) {
        quoteItems[expandedQuoteItemsIndex] = expandedQuoteItem;
      }
      yield put(
        initialBriefComponentFetch.request({
          quoteItemId: expandedQuoteItemId,
        }),
      );
    }
    yield put(receiveQuoteItems(quoteItems));
    invalidateAllBomTableQueries();
    yield put(actions.refreshQuote.success());
  } catch (error) {
    yield put(actions.refreshQuote.error(error));
  }
}

export function* watchApplyQuoteExpediteOptions({ payload }) {
  try {
    const quoteId = yield select(selectors.getQuoteId);
    const expedites = (payload.expedites || []).filter(
      (expedite) =>
        expedite && expedite.daysFaster != null && expedite.markup != null,
    );
    yield call(quoteService.applyQuoteExpediteOptions, quoteId, {
      standardLeadTimeTemplate: toFloatOrNull(payload.standardLeadTime),
      expediteTemplates: expedites,
    });
    yield put(collapseQuoteItem.request());
    const formattedNumber = yield select(selectors.getQuoteNumberWithRevision);
    const quoteItems = yield call(
      quoteService.getQuoteItemHeadersByNumber,
      formattedNumber,
    );
    const quantities = quoteItems.reduce(
      (acc, quoteItem) => [...acc, ...quoteItem.rootComponent.quantities],
      [],
    );
    yield put(receiveQuantities(quantities));
    yield put(actions.applyQuoteExpediteOptions.success());
  } catch (error) {
    yield put(actions.applyQuoteExpediteOptions.error({ error }));
  }
}

export function* watchClearQuoteExpediteOptions() {
  try {
    const quoteId = yield select(selectors.getQuoteId);
    const quoteItemIds = yield select(selectors.getQuoteItemIds);
    yield call(quoteService.clearQuoteExpediteOptions, quoteId);
    yield put(actions.clearQuoteExpediteOptions.success());
    // remove expedites on all quantities
    const nextQuantities = [];
    for (const quoteItemId of quoteItemIds) {
      const quoteItem = yield select(
        selectors.getQuoteItemDenormalized,
        quoteItemId,
      );
      const quantities = quoteItem.rootComponent.quantities.map((qty) => ({
        ...qty,
        expedites: [],
      }));
      nextQuantities.push(...quantities);
    }
    yield put(actions.receiveQuantities(nextQuantities));
  } catch (error) {
    yield put(actions.clearQuoteExpediteOptions.error({ error }));
  }
}

function* watchDeleteQuote(action) {
  yield put(actions.deleteQuote.request());
  const { quoteId, redirectToQuoteList, onSettledCallback } = action.payload;
  try {
    const expandedQuoteItemId = yield select(getExpandedQuoteItemId);
    if (expandedQuoteItemId) {
      yield put(collapseQuoteItem.request());
    }
    yield call(quoteService.requestStatusChange, quoteId, { status: 'trash' });
    if (redirectToQuoteList) {
      history.push(`/quotes/${QUOTE_STATUS.ALL}`);
    }
    yield put(actions.deleteQuote.success());
    yield call(toast, {
      status: 'success',
      title: 'Moved quote to trash',
    });
  } catch (error) {
    yield put(actions.deleteQuote.error({ error }));
    yield call(toast, {
      status: 'error',
      title: 'Error',
      description: getErrorMessage(error),
    });
  } finally {
    if (onSettledCallback) {
      yield call(onSettledCallback);
    }
  }
}

function* watchCancelQuote() {
  const isCancelConfirmed = yield call(cancelQuoteFlow);
  if (!isCancelConfirmed) {
    return null;
  }
  try {
    const quoteId = yield select(selectors.getQuoteId);
    const response = yield call(quoteService.requestStatusChange, quoteId, {
      status: 'cancelled',
    });
    const expandedQuoteItemId = yield select(getExpandedQuoteItemId);
    if (expandedQuoteItemId) {
      yield put(collapseQuoteItem.request());
    }
    yield put(actions.receiveQuote(response));
    yield put(actions.cancelQuote.success());
    toast({
      status: 'success',
      title: 'Canceled quote',
    });
  } catch (error) {
    yield put(actions.cancelQuote.error({ error }));
  }
}

function* watchMarkQuoteSent(action) {
  try {
    const quoteId = yield select(selectors.getQuoteId);
    const response = yield call(quoteService.requestStatusChange, quoteId, {
      status: 'outstanding',
    });
    yield put(actions.receiveQuote(response));
    yield put(actions.markQuoteSent.success());
  } catch (error) {
    yield put(actions.markQuoteSent.error(error));
  } finally {
    const { onSettledCallback } = action.payload;
    if (onSettledCallback) {
      yield call(onSettledCallback);
    }
  }
}

function* watchMarkQuoteLost(action) {
  try {
    const quoteId = yield select(selectors.getQuoteId);
    const response = yield call(quoteService.requestStatusChange, quoteId, {
      status: 'lost',
    });
    const expandedQuoteItemId = yield select(getExpandedQuoteItemId);
    if (expandedQuoteItemId) {
      yield put(collapseQuoteItem.request());
    }
    yield put(actions.receiveQuote(response));
    yield put(actions.markQuoteLost.success());
    toast({
      status: 'success',
      title: 'Quote marked as lost',
    });
  } catch (error) {
    yield put(actions.markQuoteLost.error({ error }));
  } finally {
    const { onSettledCallback } = action.payload;
    if (onSettledCallback) {
      yield call(onSettledCallback);
    }
  }
}

function* watchCreateRevision(action) {
  try {
    const { quoteId } = action.payload;
    const response = yield call(quoteService.createRevision, quoteId);
    const revised = response.revised;
    const quoteNumWithRev = `${revised.revisionQuoteNumber}-${revised.revisionNumber}`;
    history.push(`/quotes/edit/${quoteNumWithRev}`);
    yield put(resetQuoteItemsStore());
    // refresh quote items
    const quoteItems = yield call(
      quoteService.getQuoteItemHeadersByNumber,
      quoteNumWithRev,
    );
    yield put(receiveQuoteItems(quoteItems));
    yield put(actions.createRevision.success());
  } catch (error) {
    yield put(actions.createRevision.error(error));
  }
}

function* watchCopyQuote(action) {
  try {
    const { openInNewTab, quoteId } = action.payload;
    const response = yield call(quoteService.copy, quoteId);
    yield put(actions.copyQuote.success());
    yield call(toast, {
      status: 'success',
      title: 'Copied quote to draft',
    });
    const redirectUrl = `/quotes/edit/${response.quoteCopyNumber}`;
    if (openInNewTab) {
      yield call(window.open, redirectUrl, '_blank');
    } else {
      history.push(redirectUrl);
    }
  } catch (error) {
    yield put(actions.copyQuote.error({ error }));
  }
}

function* cancelQuoteFlow() {
  yield put({
    type: types.OPEN_CANCEL_QUOTE_CONFIRMATION_MODAL,
  });
  const { ok } = yield race({
    ok: take(types.CONFIRM_CANCEL_QUOTE),
    close: take(types.CLOSE_CANCEL_QUOTE_CONFIRMATION_MODAL),
  });
  return !!ok;
}

function* watchFinalizeQuote(action) {
  const { resendQuote, quoteId } = action.payload;
  try {
    if (!resendQuote) {
      const quote = yield select(selectors.getQuote);
      if (yield select(selectors.getHasContactConflict)) {
        yield put(actions.contactConflict.openModal());
        return;
      }

      yield put(actions.finalizeQuote.request());
      if (path(['contact', 'id'], quote)) {
        yield put(finalizeModal.open({ quoteId: quote.id }));
      } else {
        yield put(
          actions.finalizeQuote.error({
            error: 'Please select contact to finalize this quote.',
          }),
        );
      }
    } else {
      yield put(finalizeModal.open({ isResend: resendQuote, quoteId }));
    }
  } catch (error) {
    yield put(actions.finalizeQuote.error({ error }));
  }
}

function* watchSendQuote(action) {
  try {
    const { emailData, isResend, quoteId } = action.payload;
    const response = yield call(quoteService.send, quoteId, emailData);
    yield put(actions.receiveQuote(response));
    yield put(actions.sendQuote.success());
    yield put(finalizeModal.close());
    if (!isResend) {
      history.push(`/quotes/${QUOTE_STATUS.ALL}`);
    }
    invalidateAllQuoteHeaderQueries();
    invalidateAllQuoteFileQueries();
  } catch (error) {
    yield put(actions.sendQuote.error({ error }));
  }
}

// Unified finalize submission for the new Finalize Quote modal. Persists the
// expiration date first, then sends the email or marks the quote as sent.
// Reuses the existing send/mark-sent toasts. When downloadPdf is requested the
// modal stays open and the component drives the PDF download + close/redirect
// (via onSuccess); otherwise we close and redirect like the send flow.
function* watchFinalizeQuoteSubmit(action) {
  const {
    sendEmail,
    emailData,
    expiredDate,
    downloadPdf,
    isResend,
    quoteId,
    onSuccess,
    onError,
  } = action.payload;
  try {
    if (expiredDate) {
      const fullQuote = yield select(selectors.getQuote);
      yield call(quoteService.update, formatQuoteId(fullQuote), {
        expiredDate,
      });
    }

    const response = sendEmail
      ? yield call(quoteService.send, quoteId, emailData)
      : yield call(quoteService.requestStatusChange, quoteId, {
          status: 'outstanding',
        });

    yield put(actions.receiveQuote(response));
    yield put(
      sendEmail ? actions.sendQuote.success() : actions.markQuoteSent.success(),
    );
    invalidateAllQuoteHeaderQueries();
    invalidateAllQuoteFileQueries();

    if (downloadPdf) {
      if (onSuccess) {
        yield call(onSuccess);
      }
    } else {
      yield put(finalizeModal.close());
      // only redirect to the quote list when an email was actually sent;
      // mark-as-sent (and resend) keep the user on the current quote
      if (sendEmail && !isResend) {
        history.push(`/quotes/${QUOTE_STATUS.ALL}`);
      }
    }
  } catch (error) {
    yield put(
      sendEmail
        ? actions.sendQuote.error({ error })
        : actions.markQuoteSent.error(error),
    );
    if (onError) {
      yield call(onError, error);
    }
  }
}

function* watchChangeTax(action) {
  const { taxRate } = action.payload;
  try {
    const quote = yield call(getOrCreateQuote);
    yield put(APP.actions.pagePending.start());
    const savedQuote = yield call(quoteService.update, formatQuoteId(quote), {
      taxRate,
    });
    yield put(actions.changeTax.success());
    yield put(actions.receiveQuote(savedQuote));
  } catch (error) {
    yield put(actions.changeTax.error(error, nanoid()));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchChangeExpiredDate(action) {
  const { expiredDate } = action.payload;
  const expired = moment(expiredDate).isBefore(moment().endOf('day'));
  try {
    const prevQuote = yield call(getOrCreateQuote);
    const quote = { ...prevQuote, expiredDate, expired };
    yield put(actions.receiveQuote(quote));
    yield call(delay, 300);
    yield put(APP.actions.pagePending.start());
    yield call(quoteService.update, formatQuoteId(quote), {
      expiredDate: quote.expiredDate,
    });
  } catch (error) {
    console.error(error);
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchChangeDueDate(action) {
  const { dueDate } = action.payload;
  if (dueDate) {
    try {
      const quote = yield call(getOrCreateQuote);
      quote.dueDate = dueDate;
      yield put(actions.receiveQuote(quote));
      yield call(delay, 300); //why are we doing this?

      yield put(APP.actions.pagePending.start());
      yield call(quoteService.update, formatQuoteId(quote), {
        dueDate: dueDate,
      });
    } catch (error) {
      yield put(changeDueDate.error(error));
    } finally {
      yield put(APP.actions.pagePending.finish());
    }
  }
}

function* watchChangeRequestedDate(action) {
  const { requestedDate } = action.payload;

  try {
    const quote = yield call(getOrCreateQuote);
    quote.manualRfqReceivedDate = requestedDate;
    yield put(actions.receiveQuote(quote));
    yield call(delay, 300);

    yield put(APP.actions.pagePending.start());
    yield call(quoteService.update, formatQuoteId(quote), {
      manualRfqReceivedDate: requestedDate,
    });
  } catch (error) {
    yield put(changeRequestedDate.error(error));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchQuantityUpdate() {
  const chan = yield call(channel);
  yield fork(quantityUpdateWorker, chan);
  for (;;) {
    const { payload } = yield take(types.QUANTITY_UPDATE_REQUEST);
    const tempId = nanoid();
    yield call(performOptimisticQuantityUpdateSaga, payload, tempId);
    yield put(chan, { tempId, payload });
  }
}

function* quantityUpdateWorker(chan) {
  let updateTask;
  const idMap = new Map();
  for (;;) {
    const { tempId, payload } = yield take(chan);
    payload.data.id = idMap.get(payload.data.id) || payload.data.id;
    const updateType = getQuantityUpdateType(payload.data);
    if (updateType === 'UPDATE') {
      if (updateTask) yield cancel(updateTask);
      updateTask = yield fork(performActualQuantityUpdateSaga, payload, tempId);
    } else if (updateType === 'CREATE' || updateType === 'DELETE') {
      if (updateTask) yield join(updateTask);
      yield call(performActualQuantityUpdateSaga, payload, tempId, idMap);
    }
  }
}

function* performOptimisticQuantityUpdateSaga(payload, tempId) {
  const updateType = getQuantityUpdateType(payload.data);
  if (updateType === 'CREATE') {
    const fakeData = { ...payload.data, id: tempId, expedites: [] };
    const component = yield select(
      selectors.getComponentById,
      payload.componentId,
    );
    const nextComponent = { ...component };
    nextComponent.quantities.push(fakeData.id);
    yield put(
      actions.updateQuantity.success({
        component: nextComponent,
        quoteItemQuantity: fakeData,
      }),
    );
  } else if (updateType === 'UPDATE') {
    yield put(
      actions.updateQuantity.success({ quoteItemQuantity: payload.data }),
    );
  } else if (updateType === 'DELETE') {
    const component = yield select(
      selectors.getComponentById,
      payload.componentId,
    );
    const nextComponent = { ...component };
    nextComponent.quantities = nextComponent.quantities.filter(
      (quantityId) => quantityId != payload.data.id,
    );
    yield put(actions.updateQuantity.success({ component: nextComponent }));
  }
}

function* performActualQuantityUpdateSaga(payload, tempId, idMap) {
  try {
    yield put(APP.actions.pagePending.start());
    const updateType = getQuantityUpdateType(payload.data);
    if (updateType === 'CREATE') {
      const response = yield call(
        newQuoteItemService.quantities.create,
        payload.componentId,
        payload.data.quantity,
      );
      const component = yield select(
        selectors.getComponentById,
        payload.componentId,
      );
      const nextComponent = { ...component };
      nextComponent.quantities = nextComponent.quantities.map((quantity) =>
        quantity.id === tempId ? response.id : quantity,
      );
      idMap.set(tempId, response.id);
      const { quantity } = yield select(selectors.getQuantity, tempId);

      yield put(
        actions.updateQuantity.commit({
          quoteItemQuantity: {
            ...response,
            quantity,
          },
          component: nextComponent,
        }),
      );
    } else if (updateType === 'UPDATE') {
      yield call(delay, 300);
      if (
        !isBlank(payload.data.priceAdjustment) &&
        isNaN(parseFloat(payload.data.priceAdjustment))
      )
        return; // HACK
      const response = yield call(
        newQuoteItemService.quantities.update,
        payload.data.id,
        payload.data,
      );
      yield put(actions.updateQuantity.commit({ quoteItemQuantity: response }));
    } else if (updateType === 'DELETE') {
      yield call(newQuoteItemService.quantities.delete, payload.data.id);
      const component = yield select(
        selectors.getComponentById,
        payload.componentId,
      );
      const nextComponent = { ...component };
      nextComponent.quantities = nextComponent.quantities.filter(
        (quantity) => quantity.id != payload.data.id,
      );
      yield put(actions.updateQuantity.commit({ component: nextComponent }));
    }
  } catch (error) {
    yield put(actions.updateQuantity.error({ error }));
    console.error(error);
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function getQuantityUpdateType(data) {
  if (data.id && !data.quantity) {
    return 'DELETE';
  } else if (data.id) {
    return 'UPDATE';
  } else if (!data.id && data.quantity) {
    return 'CREATE';
  }
  return null;
}

function* watchExpeditesUpdate() {
  const chan = yield actionChannel(types.EXPEDITES_UPDATE_REQUEST);
  for (;;) {
    const action = yield take(chan);
    const { quantityId, values } = action.payload;
    yield call(updateExpeditesSaga, quantityId, values);
  }
}

function* watchApplyExpeditesToAll(action) {
  const { from, to } = action.payload;
  for (let i = 0; i < to.length; i++) {
    if (to[i].id != from.id) {
      yield call(pasteExpeditesSaga, from, to[i]);
    }
  }
  yield put(actions.finishUpdatingExpediteOptions());
}

function* watchPasteExpedites(action) {
  const copiedExpediteOptionsRow = yield select(
    selectors.getExpediteOptionsRowBuffer,
  );
  yield call(pasteExpeditesSaga, copiedExpediteOptionsRow, action.payload);
  yield put(actions.finishUpdatingExpediteOptions());
}

function* watchPasteAllExpedites(action) {
  const copiedAllExpediteOptions = yield select(
    selectors.getAllExpediteOptionsBuffer,
  );
  for (let i = 0; i < action.payload.length; i++) {
    if (copiedAllExpediteOptions[i]) {
      yield call(
        pasteExpeditesSaga,
        copiedAllExpediteOptions[i],
        action.payload[i],
      );
    }
  }
  yield put(actions.finishUpdatingExpediteOptions());
}

function* pasteExpeditesSaga(from, to) {
  yield call(updateExpeditesSaga, to.id, from.expedites);
  const data = {
    ...to,
    manualLeadTime: from.manualLeadTime,
  };
  yield call(performActualQuantityUpdateSaga, { data });
}

function* watchClearExpedites(action) {
  yield call(clearExpeditesSaga, action.payload);
  yield put(actions.finishUpdatingExpediteOptions());
}

function* watchClearAllExpedites(action) {
  for (let i = 0; i < action.payload.length; i++) {
    yield call(clearExpeditesSaga, action.payload[i]);
  }
  yield put(actions.finishUpdatingExpediteOptions());
}

function* clearExpeditesSaga(quantity) {
  yield call(updateExpeditesSaga, quantity.id, []);
}

function* updateExpeditesSaga(quantityId, values) {
  try {
    yield put(APP.actions.pagePending.start());
    const initialQuantity = yield select(
      selectors.getQuantityDenormalized,
      quantityId,
    );
    let quantity = initialQuantity;
    for (let index = 0; index < 3; index++) {
      const expedite = values[index];
      const expediteFromStore = quantity.expedites[index];
      if (expediteFromStore && !expedite) {
        // delete expedite
        yield call(
          quoteService.quoteItems.deleteExpedite,
          expediteFromStore.id,
        );
        quantity = assocPath(['expedites', index], null, quantity);
      } else if (
        expedite &&
        expediteFromStore &&
        isValidExpedite(expedite) &&
        !isExpediteEqual(expedite, expediteFromStore)
      ) {
        // update expedite
        const response = yield call(
          quoteService.quoteItems.updateExpedite,
          expediteFromStore.id,
          expedite,
        );
        quantity = assocPath(['expedites', index], response, quantity);
      } else if (expedite && !expediteFromStore && isValidExpedite(expedite)) {
        // create expedite
        const response = yield call(
          quoteService.quoteItems.createExpedite,
          quantityId,
          expedite,
        );
        quantity = assocPath(['expedites', index], response, quantity);
      }
    }
    if (quantity !== initialQuantity) {
      quantity.expedites = quantity.expedites.filter((x) => x !== null);
      yield put(
        actions.updateExpedites.success({ quoteItemQuantity: quantity }),
      );
    }
  } catch (error) {
    yield put(actions.updateExpedites.error({ error }));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchFeaturesUpdate(action) {
  try {
    const { componentId, values } = action.payload;
    yield put(APP.actions.pagePending.start());
    const response = yield call(quoteService.components.update, componentId, {
      features: values,
    });
    yield put(
      actions.updateFeatures.success({
        quoteItemComponent: { ...response, part: response.part.uuid },
      }),
    );
  } catch (error) {
    yield put(actions.updateFeatures.error({ error }));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchOverridesUpdate(action) {
  try {
    const { componentId, values } = action.payload;
    yield put(APP.actions.pagePending.start());
    const component = yield select(selectors.getComponentById, componentId);
    const nextComponent = { ...component, overrides: values };
    yield put(
      actions.updateOverrides.success({ quoteItemComponent: nextComponent }),
    );
    yield call(delay, 200);
    const response = yield call(quoteService.components.update, componentId, {
      overrides: values,
    });
    yield put(
      actions.updateOverrides.commit({
        quoteItemComponent: { ...response, part: response.part.uuid },
      }),
    );
  } catch (error) {
    yield put(actions.updateOverrides.error({ error }));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchPrivateNotesUpdate(action) {
  try {
    const { quoteItemId, orderItemId, value } = action.payload;
    yield put(APP.actions.pagePending.start());
    const response = yield call(
      quoteService.quoteItems.updatePrivateNotes,
      quoteItemId,
      value,
    );
    const quoteItem = yield select(
      selectors.getQuoteItemById,
      orderItemId || quoteItemId,
    );
    const newQuoteItem = { ...quoteItem, privateNotes: response.text };
    yield put(actions.updatePrivateNotes.success({ quoteItem: newQuoteItem }));
  } catch (error) {
    yield put(actions.updatePrivateNotes.error({ error }));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchPublicNotesUpdate(action) {
  try {
    const { quoteItemId, value } = action.payload;
    yield put(APP.actions.pagePending.start());
    const response = yield call(
      quoteService.quoteItems.updatePublicNotes,
      quoteItemId,
      value,
    );
    const quoteItem = yield select(selectors.getQuoteItemById, quoteItemId);
    const newQuoteItem = { ...quoteItem, publicNotes: response.text };
    yield put(actions.updatePublicNotes.success({ quoteItem: newQuoteItem }));
  } catch (error) {
    yield put(actions.updatePublicNotes.error({ error }));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

export function* watchSelectEstimator(action) {
  try {
    const quote = yield call(getOrCreateQuote);
    const { estimator } = action.payload;
    const savedQuote = yield call(quoteService.update, formatQuoteId(quote), {
      estimator,
    });
    yield put(actions.receiveQuote(savedQuote));
    yield put(actions.selectEstimator.success());
  } catch (error) {
    console.error(error);
    yield put(actions.selectEstimator.error(error));
  }
}

export function* watchSelectSalesperson(action) {
  try {
    const quote = yield call(getOrCreateQuote);
    const { salesperson } = action.payload;
    const savedQuote = yield call(quoteService.update, formatQuoteId(quote), {
      salesperson,
    });
    yield put(actions.receiveQuote(savedQuote));
    yield put(actions.selectSalesperson.success());
  } catch (error) {
    console.error(error);
    yield put(actions.selectSalesperson.error(error));
  }
}

export function* watchSelectQuotePriority(action) {
  try {
    const quote = yield call(getOrCreateQuote);
    const { priority } = action.payload;
    const savedQuote = yield call(quoteService.update, formatQuoteId(quote), {
      priority: priority || null,
    });
    yield put(actions.receiveQuote(savedQuote));
    yield put(actions.changePriority.success());
  } catch (error) {
    console.error(error);
    yield put(actions.changePriority.error(error));
  }
}

export function* watchSelectSupplierFacility(action) {
  try {
    const quote = yield call(getOrCreateQuote);
    const { supplierFacilityId } = action.payload;
    const savedQuote = yield call(quoteService.update, formatQuoteId(quote), {
      supplierFacility: supplierFacilityId,
    });
    yield put(actions.receiveQuote(savedQuote));
    yield put(actions.selectSupplierFacility.success());
  } catch (error) {
    console.error(error);
    yield put(actions.selectSupplierFacility.error(error));
  }
}

export function* watchSelectContact(action) {
  try {
    const quote = yield call(getOrCreateQuote);
    // resolve rfq contact conflict if it exists
    const hasRfqConflict = yield select(selectors.getHasContactConflict);
    if (hasRfqConflict) {
      const requestForQuote = yield select(selectors.getRequestForQuoteInfo);
      yield call(quoteService.resolveContactConflict, requestForQuote.id);
    }

    const { contactId } = action.payload;
    const savedQuote = yield call(quoteService.update, formatQuoteId(quote), {
      contact: contactId,
    });
    yield put(actions.receiveQuote(savedQuote));
    yield put(actions.selectContact.success());
  } catch (error) {
    console.error(error);
    yield put(actions.selectContact.error(error));
  }
}

// Todo: remove. This is a terrible paradigm to follow.
export const constructTempQuoteItemData = (filename, position, quoteId) => {
  return {
    id: nanoid(),
    rootComponent: {
      id: nanoid(),
      part: {
        uuid: `${TEMP_PART_UUID_PREFIX}${nanoid()}`,
        filename,
      },
    },
    position,
    workflowStatus: QUOTE_ITEM_STATUS.NOT_STARTED,
    quoteId,
  };
};

// ToDo: update this to point to a saga in quote items app, and then delete this
function* watchUpload(action) {
  try {
    const opts = action.payload;

    let fileInfoList;
    try {
      fileInfoList = yield call(fileUploadSaga, [].concat(opts.file));
    } catch (error) {
      yield put(actions.uploadError({ error }));
      return;
    }

    if (fileInfoList.length === 0) {
      return;
    }

    const quoteSettings = yield call(detailsSettingsModalFlow, {
      isMultipleItems: fileInfoList.length > 1,
      mode: DETAILS_SETTINGS_MODAL_TYPES.QUOTE_ITEM_UPLOAD,
      filenames: Object.values(fileInfoList).flatMap(({ file }) => file.name),
    });

    if (!quoteSettings) {
      return;
    }

    const { shouldMergeIdenticallyNamedFiles } = quoteSettings;

    const lineItemsFileInfo = buildLineItemsFileInfo({
      fileInfos: fileInfoList,
      shouldMergeIdenticallyNamedFiles,
    });

    let quote;
    try {
      quote = yield call(getOrCreateQuote);
    } catch (error) {
      console.error(error);
      yield put(actions.uploadError({ error }));
      return;
    }

    const quoteId = quote.id;

    yield put(
      actions.saveQuoteSettingsById({
        quoteWithSettingsId: quoteId,
        quoteSettings: quoteSettings || {},
      }),
    );

    // insert temporary quote items into the quote that optimistically updates
    // the loading state of the user, in case the file takes a long time to upload
    const oldQuoteItems = yield select(selectors.getQuoteItems);
    const infoIdToTempQuoteItemId = {};
    const lineItemPosition = opts.position ?? oldQuoteItems.length + 1;
    const newTempQuoteItems = lineItemsFileInfo.map(
      ({ primaryFileInfo }, index) => {
        const tempQuoteItem = constructTempQuoteItemData(
          primaryFileInfo.file.name,
          lineItemPosition + index,
          quoteId,
        );
        infoIdToTempQuoteItemId[primaryFileInfo.tempId] = tempQuoteItem.id;
        return tempQuoteItem;
      },
    );

    const newQuoteItems = createTemporaryQuoteItems({
      lineItemPosition,
      newTempQuoteItems,
      oldQuoteItems,
    });

    const oldTempQuoteItemIds = yield select(getTemporaryQuoteItemIds);
    yield all([
      put(
        receiveTemporaryQuoteItemIds({
          temporaryQuoteItemIds: oldTempQuoteItemIds.concat(
            newTempQuoteItems.map((qi) => qi.id),
          ),
        }),
      ),

      put(receiveQuoteItems(newQuoteItems)),
    ]);

    const index = 0;
    // upload all files and set real part id
    for (const lineItemFileInfo of lineItemsFileInfo) {
      const { primaryFileInfo, supportingFileInfos } = lineItemFileInfo;
      const formData = yield call(uploadSaga, {
        ...opts,
        position:
          opts.position == undefined ? undefined : opts.position + index,
        file: primaryFileInfo.file,
        thickness: primaryFileInfo.thickness,
        units: primaryFileInfo.units,
        quoteSettings,
      });

      let response;
      try {
        response = yield call(newQuoteItemService.upload, quoteId, formData);

        const partUUID = response.rootComponent.part.uuid;

        if (supportingFileInfos.length > 0) {
          const formData = new FormData();
          const uploadData = {};
          supportingFileInfos.forEach((fileInfo) => {
            formData.append(fileInfo.file.name, fileInfo.file);
            uploadData[fileInfo.file.name] = {
              units: fileInfo.units,
              thickness: fileInfo.thickness,
            };
          });
          formData.append('upload_data', JSON.stringify(uploadData));
          formData.append('origin_quote_item_id', response.id);
          formData.append('part_master_version', response.partMasterVersion);
          yield call(partService.attachFile, partUUID, formData);
        }
      } catch (error) {
        console.error(error);

        yield put(
          actions.uploadError({
            error: `${primaryFileInfo.file.name} cannot be uploaded. ${error?.message}`,
          }),
        );
        const intermediateQuoteItems = yield select(selectors.getQuoteItems);
        const oldTempIds = yield select(getTemporaryQuoteItemIds);
        yield put(
          receiveQuoteItems(
            intermediateQuoteItems.filter(
              (qid) => qid !== infoIdToTempQuoteItemId[primaryFileInfo.tempId],
            ),
          ),
        );
        yield put(
          receiveTemporaryQuoteItemIds({
            temporaryQuoteItemIds: oldTempIds.filter(
              (id) => id !== infoIdToTempQuoteItemId[primaryFileInfo.tempId],
            ),
          }),
        );
        continue;
      }

      response.workflowStatus = QUOTE_ITEM_STATUS.NOT_STARTED;

      // before adding quote item, update meta cache for loading
      const nextProcessingStatuses = {
        [response.rootComponent.id.toString()]: response.rootComponent.status,
      };
      yield put(
        componentStatuses.receiveProcessingStatuses({
          processingStatuses: nextProcessingStatuses,
        }),
      );

      const intermediateQuoteItems = yield select(selectors.getQuoteItems);
      const oldTempIds = yield select(getTemporaryQuoteItemIds);

      // add new quote item and remove corresponding temporary quote item
      yield put(
        receiveQuoteItems(
          intermediateQuoteItems
            .map((qi) => {
              if (qi.id === infoIdToTempQuoteItemId[primaryFileInfo.tempId]) {
                qi.quoteId = null;
                return qi;
              }
              return qi;
            })
            .concat([response]),
        ),
      );
      yield put(
        receiveTemporaryQuoteItemIds({
          temporaryQuoteItemIds: oldTempIds.filter(
            (id) => id !== infoIdToTempQuoteItemId[primaryFileInfo.tempId],
          ),
        }),
      );
    }

    yield put(actions.uploadSuccess());
  } catch (e) {
    console.error(e);
  }
}

function uploadSaga(opts) {
  const data = {
    units: opts.units,
    thickness: opts.thickness,
    file: opts.file,
    position: opts.position,
    processId: opts.quoteSettings.process,
    materialId: opts.quoteSettings.material,
    supplierMaterialId: opts.quoteSettings.supplierMaterial,
    finishOpDefIds: opts.quoteSettings.finishOpDefIds,
    quantities: opts.quoteSettings.quantities,
    exportControlled: opts.quoteSettings.exportControlled,
    estimatorId: opts.quoteSettings.estimatorId,
    partNumber: opts.quoteSettings.partNumber,
    revision: opts.quoteSettings.revision,
  };
  return getFormData(data);
}

function* watchCustomerFacingNotesUpdate(action) {
  try {
    const { notes } = action.payload;
    const prevQuote = yield call(getOrCreateQuote);
    const quote = { ...prevQuote, quoteNotes: notes };
    yield put(actions.receiveQuote(quote));
    yield put(APP.actions.pagePending.start());
    yield call(quoteService.updateCustomerFacingNotes, quote.id, notes);
  } catch (error) {
    console.error(error);
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* watchQuotesPrivateNotesUpdate(action) {
  try {
    const { notes, toast } = action.payload;
    const prevQuote = yield call(getOrCreateQuote);
    const quote = { ...prevQuote, privateNotes: notes };
    yield put(actions.receiveQuote(quote));
    yield put(APP.actions.pagePending.start());
    yield call(quoteService.updatePrivateNotes, quote.id, notes);
    yield put(actions.updateQuotesPrivateNotes.success({ toast }));
  } catch (error) {
    yield put(actions.updateQuotesPrivateNotes.error({ error }));
  } finally {
    yield put(APP.actions.pagePending.finish());
  }
}

function* fetchEmailTemplatesSaga(quoteIdIn) {
  try {
    let quoteId = quoteIdIn;
    if (!quoteIdIn) {
      quoteId = yield select(selectors.getQuoteId);
    }
    if (quoteId) {
      const response = yield call(quoteService.getEmailTemplates, quoteId);
      if (response) {
        yield put(
          actions.fetchEmailTemplates.success({
            emailTemplates: response.emailTemplates,
          }),
        );
      }
    }
  } catch (error) {
    yield put(actions.fetchEmailTemplates.error({ error }));
  }
}

function* watchContactConflict() {
  while (true) {
    try {
      const action = yield take(types.CONTACT_CONFLICT_RESOLVE);
      const quote = yield select(selectors.getQuote);
      yield call(quoteService.resolveContactConflict, quote.requestForQuote.id);
      const quoteHeader = yield call(
        quoteService.getHeaderByNumber,
        formatQuoteId(quote),
      );
      yield put(actions.receiveQuote(quoteHeader));
      yield put(actions.contactConflict.success());
      if (action.payload.buttonClicked === 'use') {
        yield put(actions.contactConflict.closeModal());
      }
    } catch (error) {
      yield put(actions.contactConflict.error(error));
    }
  }
}

export const reorderQuoteItems = (quoteItems, oldPosition, newPosition) => {
  const updatePosition = (list) =>
    list.slice().map((item, index) => assoc('position', index + 1, item));

  let result = quoteItems.slice().sort(createNumericComparator('position'));
  result = updatePosition(result);

  const target = result[oldPosition - 1];

  result.splice(oldPosition - 1, 1);
  result.splice(newPosition - 1, 0, target);

  return updatePosition(result);
};

function* watchReorderQuoteItemsFlow(action) {
  const { position } = action.payload;
  const expandedId = yield select(getExpandedQuoteItemId);
  let quoteItems = yield select(selectors.getDenormalizedQuoteItems);
  const quoteItem = quoteItems[position - 1];

  if (!quoteItem) {
    return;
  }

  if (expandedId === quoteItem.id) {
    yield put(collapseQuoteItem.request());
  }

  const requestAction = yield take(types.REORDER_QUOTE_ITEMS_REQUEST);
  const { nextPosition } = requestAction.payload;

  // refresh quoteItems since the list might have changed
  quoteItems = yield select(selectors.getDenormalizedQuoteItems);

  // make sure that we're not trying to move the item below
  // another item that's still uploading
  let isUploading = false;
  quoteItems.slice(0, nextPosition).forEach((quoteItem) => {
    isUploading = isUploading || !!quoteItem.isFetching;
  });
  if (isUploading || position === nextPosition) {
    // return quote with original order since the local copy might have changed
    yield put(actions.reorderQuoteItems.success(quoteItems));
    return;
  }

  const reorderedQuoteItems = reorderQuoteItems(
    quoteItems,
    position,
    nextPosition,
  );
  yield put(actions.reorderQuoteItems.success(reorderedQuoteItems));

  try {
    yield call(quoteService.quoteItems.update, quoteItem.id, {
      position: nextPosition,
    });
  } catch (error) {
    console.error(error);
  }
}

export function* watchDownloadNestPDF(action) {
  const waitId = nanoid();
  try {
    const { quoteId, nestId } = action.payload;
    const response = yield call(quoteService.downloadNestPDF, quoteId, nestId);
    yield call(toast.close, waitId);
    switch (response.status) {
      case 'ready': {
        yield call(window.open, response.authenticatedDownloadUrl, '_blank');
        break;
      }
      case 'processing':
      case 'failed': {
        yield call(toast, {
          status: 'error',
          title: 'Download failed',
          description: response.message,
        });
        break;
      }
      case 'initiated': {
        yield call(toast, {
          status: 'info',
          title: 'PDF generation started',
          description: response.message,
        });
        break;
      }
    }
    yield put(actions.downloadNestPDF.success());
  } catch (error) {
    yield put(actions.downloadNestPDF.error(error));
    yield call(toast.close, waitId);
    yield call(toast, {
      status: 'error',
      title: 'Download failed',
      description: 'Check your connection and try again.',
      duration: null,
    });
  }
}

function* watchCreateQuoteCSV() {
  const quote = yield select(selectors.getQuote);
  const groupId = yield select(SESSION.selectors.getGroupId);
  const { number: quoteNumber, revisionNumber: quoteRevisionNumber } = quote;
  const params = quoteRevisionNumber
    ? { group: groupId, revisionNumber: quoteRevisionNumber }
    : { group: groupId };
  try {
    yield call(quoteService.downloadCsvByNumber, quoteNumber, params);
    yield put(actions.createQuoteCSV.success());
  } catch (e) {
    yield put(actions.createQuoteCSV.error());
  }
}

function* watchBAQInitializing({ payload }) {
  performance.mark(LOAD_BAQ.START);
  const { revisionNumber, quoteNumber } = payload;
  yield call(initializeBAQPage, { revisionNumber, quoteNumber });
}

/**
 * Iterates through quote items and pulls out the primary file uuid. Then pings
 * the backend to check for primary file processing and interrogation status.
 * @param quoteItems fully detailed quote items response
 */
export function* checkFileStatusesForQuoteItems(quoteItems) {
  const primaryFileUuids = quoteItems.map(
    (qi) => qi.rootComponent.part.primaryFileUuid,
  );
  if (primaryFileUuids.length) {
    const response = yield call(partService.checkStatuses, primaryFileUuids);
    const { processing, interrogation } = response.statuses;
    const nextProcessingStatuses = {};
    const nextInterrogationStatuses = {};
    for (const i in processing) {
      const componentId = quoteItems[i].rootComponent.id.toString();
      nextProcessingStatuses[componentId] = {
        ...processing[i],
      };
      nextInterrogationStatuses[componentId] = {
        ...interrogation[i],
      };
    }
    yield all([
      put(
        componentStatuses.receiveProcessingStatuses({
          processingStatuses: nextProcessingStatuses,
        }),
      ),
      put(
        componentStatuses.receiveInterrogationStatuses({
          interrogationStatuses: nextInterrogationStatuses,
        }),
      ),
    ]);
  }
}

/**
 * Loads the quote items and other associated quote information that isn't the header
 */
function* initializeBAQPage({ quoteNumber, revisionNumber }) {
  try {
    const quoteNumberWithRevision = revisionNumber
      ? `${quoteNumber}-${revisionNumber}`
      : quoteNumber;

    const { opDefIds, quoteItems, quoteWorkflows } = yield all({
      opDefIds: call(fetchOpDefs),
      quoteItems: quoteNumber
        ? call(
            quoteService.getQuoteItemHeadersByNumber,
            quoteNumberWithRevision,
          )
        : [],
      quoteWorkflows: call(processesService.workflows.list, {
        type: 'quote',
      }),
      entities: call(fetchEntities),
      globalExpedites: call(watchFetchGlobalExpediteOptions),
      materials: call(initMaterialStore),
      processes: call(fetchProcesses),
    });

    if (quoteItems.length > 0) {
      yield put(receiveQuoteItems(quoteItems));
    }

    yield call(checkFileStatusesForQuoteItems, quoteItems);

    yield put(receiveQuoteOpDefs(opDefIds));
    yield put(receiveQuoteWorkflow({ quoteWorkflow: quoteWorkflows[0] }));

    yield put(baqPageInitialization.success());
  } catch (e) {
    yield call(toast, {
      status: 'error',
      title: "Couldn't load quote",
    });
    yield put(baqPageInitialization.error());
    console.error(e);
  }
}

function* watchUpdateQuoteRfqNumber(action) {
  try {
    const quote = yield call(getOrCreateQuote);
    const { rfqNumber } = action.payload;
    const updatedQuote = yield call(quoteService.update, formatQuoteId(quote), {
      rfqNumber,
    });
    yield put(actions.updateQuoteRfqNumber.success());
    yield put(actions.receiveQuote(updatedQuote));
  } catch (e) {
    yield put(actions.updateQuoteRfqNumber.error(e));
  }
}

function* watchUpdateQuoteMetadata(action) {
  try {
    const { metadata } = action.payload;
    const quoteNumberWithRevision = yield select(
      selectors.getQuoteNumberWithRevision,
    );
    const updatedQuote = yield call(
      quoteService.update,
      quoteNumberWithRevision,
      {
        metadata,
      },
    );
    yield put(actions.updateMetadata.success());
    yield put(actions.receiveQuote(updatedQuote));
  } catch (e) {
    yield put(actions.updateMetadata.error(e));
  }
}

function* watchExportQuoteSummary(action) {
  try {
    const { quoteId } = action.payload;
    const quoteNumber = yield select(selectors.getQuoteNumber);
    const quoteRevision = yield select(selectors.getQuoteRevisionNumber);

    const { taskId } = yield call(
      quoteService.initiateExportQuoteSummary,
      quoteId,
    );
    yield put(
      actions.addExcelExportTasks({
        tasks: [{ taskId, quoteNumber, quoteRevision }],
      }),
    );
  } catch (err) {
    console.error(
      `Quote ${action.payload.quoteId} export to Excel failed with the following error:`,
      err,
    );
    yield put(actions.exportQuoteSummary.error({}));
  }
}

function* watchRefetchQuoteItemHeaders(
  action: ReturnType<typeof actions.refetchQuoteItemHeaders.request>,
) {
  try {
    const { quoteId, quoteItemIds } = action.payload;
    const quoteItems: QuoteItemBaqData[] = yield call(
      quoteService.getQuoteItemHeadersById,
      quoteId,
      quoteItemIds,
    );
    yield put(receiveQuoteItems(quoteItems));
    yield put(actions.refetchQuoteItemHeaders.success());
  } catch (err) {
    yield put(actions.refetchQuoteItemHeaders.error({ error: err }));
  }
}
